import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios';
import { ApiError, toApiError } from '@/lib/errors';
import { tokenStore } from '@/lib/tokenStore';

/** Event bus for auth-related signals from the HTTP layer. */
type AuthEventListener = () => void;
const unauthorizedListeners = new Set<AuthEventListener>();

export function onUnauthorized(fn: AuthEventListener) {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

function emitUnauthorized() {
  unauthorizedListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // no-op
    }
  });
}

const envBase = (import.meta.env.VITE_API_URL ?? '').trim();
const baseURL = envBase ? `${envBase.replace(/\/$/, '')}/api/v1` : '/api/v1';

/**
 * Axios client configured for the BDS Ticket Hub backend.
 *
 * Features:
 * - baseURL `/api/v1` (proxied in dev to VITE_API_URL) so every API call is typed to the right origin.
 * - Request interceptor attaches the current access token.
 * - Response interceptor transparently refreshes the access token once on 401,
 *   queues in-flight requests during the refresh, and re-runs them on success.
 * - All errors thrown are normalized to {@link ApiError} (RFC 7807 when the backend returned one).
 *
 * Phase 3a-2: callers can set `_skipAuthRefresh: true` on the request config to
 * opt OUT of the automatic refresh-and-retry path AND the fallback session
 * clear. Use this for endpoints that proxy to a third-party service whose 401
 * does not mean the user's session has expired (e.g. Shopify order lookup).
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json, application/problem+json'
  }
});

let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = tokenStore.getRefreshToken();
  const payload = refreshToken ? { refresh_token: refreshToken } : {};
  // Bypass the main client so we don't recurse into interceptors.
  const response = await axios.post(`${baseURL}/auth/refresh`, payload, {
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  const data = response.data as {
    access_token?: string;
    refresh_token?: string;
    success?: boolean;
  };
  if (!data.access_token) {
    throw new AxiosError(
      'Refresh failed: no access_token in response',
      undefined,
      response.config,
      response.request,
      response
    );
  }
  tokenStore.setAccessToken(data.access_token);
  if (data.refresh_token) {
    tokenStore.setRefreshToken(data.refresh_token);
  }
  return data.access_token;
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccessToken();
  if (token && config.headers) {
    // `set` is available in axios v1 headers; guard just in case.
    if (typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError)) {
      return Promise.reject(toApiError(error));
    }

    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean; _skipAuthRefresh?: boolean })
      | undefined;
    const status = error.response?.status;

    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/refresh') ||
      originalRequest?.url?.includes('/auth/logout');

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest._skipAuthRefresh &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        if (originalRequest.headers) {
          if (typeof originalRequest.headers.set === 'function') {
            originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
          } else {
            (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
          }
        }
        return apiClient.request(originalRequest);
      } catch (refreshError) {
        tokenStore.clear();
        emitUnauthorized();
        return Promise.reject(toApiError(refreshError));
      }
    }

    if (
      status === 401 &&
      !isAuthEndpoint &&
      !originalRequest?._skipAuthRefresh
    ) {
      tokenStore.clear();
      emitUnauthorized();
    }
    return Promise.reject(toApiError(error));
  }
);

export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await apiClient.request<T>(config);
    return response.data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw toApiError(error);
  }
}

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => apiRequest<T>({ ...config, method: 'GET', url }),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'POST', url, data }),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PUT', url, data }),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PATCH', url, data }),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'DELETE', url })
};
